import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [canPurchase, setCanPurchase] = useState(false);
  const [adding, setAdding] = useState(false);
  const [resentEmails, setResentEmails] = useState({});
  const [resetEmails, setResetEmails] = useState({});
  const [editingEmail, setEditingEmail] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await api.listUsers();
      setUsers(res.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setAdding(true);
    setError('');
    try {
      await api.createUser({ email: newEmail, name: newName, canPurchaseDirectly: canPurchase, sendInvite: true });
      setNewName(''); setNewEmail(''); setCanPurchase(false); setShowAdd(false);
      await loadUsers();
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  }

  async function togglePermission(u) {
    try {
      await api.updateUser(u.email, { permissions: { canPurchaseDirectly: !u.permissions?.canPurchaseDirectly } });
      await loadUsers();
    } catch (e) { setError(e.message); }
  }

  async function toggleStatus(u) {
    const newStatus = u.status === 'disabled' ? 'active' : 'disabled';
    try {
      await api.updateUser(u.email, { status: newStatus });
      await loadUsers();
    } catch (e) { setError(e.message); }
  }

  async function resendInvite(u) {
    try {
      const res = await api.resendInvite(u.email);
      if (res.emailSent) {
        setResentEmails(prev => ({ ...prev, [u.email]: true }));
        setTimeout(() => setResentEmails(prev => ({ ...prev, [u.email]: false })), 3000);
      } else {
        alert(`User exists but email could not be sent. ${res.error || ''}`);
      }
    } catch (e) { setError(e.message); }
  }

  async function resetPassword(u) {
    try {
      const res = await api.resetPassword(u.email);
      if (res.emailSent) {
        setResetEmails(prev => ({ ...prev, [u.email]: true }));
        setTimeout(() => setResetEmails(prev => ({ ...prev, [u.email]: false })), 3000);
      } else {
        alert(`Password reset email could not be sent. ${res.error || ''}`);
      }
    } catch (e) { setError(e.message); }
  }

  if (user?.role !== 'customer_admin') {
    return <div style={s.empty}>You do not have permission to view this page.</div>;
  }

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Team Management</h1>
          <p style={s.subtitle}>Manage employee accounts and ordering permissions</p>
        </div>
        <button style={s.addBtn} onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? 'Cancel' : '+ Add Employee'}
        </button>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {showAdd && (
        <form onSubmit={handleAdd} style={s.addForm}>
          <div style={s.formRow}>
            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input style={s.input} value={newName} onChange={e => setNewName(e.target.value)} required placeholder="Employee name" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input style={s.input} type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} required placeholder="employee@email.com" />
            </div>
          </div>
          <label style={s.checkLabel}>
            <input type="checkbox" checked={canPurchase} onChange={e => setCanPurchase(e.target.checked)} />
            <span style={{ marginLeft: 8 }}>Can place orders directly (without approval)</span>
          </label>
          <button style={s.submitBtn} type="submit" disabled={adding}>
            {adding ? 'Creating...' : 'Create & Send Invite'}
          </button>
        </form>
      )}

      {loading ? (
        <div style={s.empty}>Loading team...</div>
      ) : users.length === 0 ? (
        <div style={s.empty}>No team members yet. Click "Add Employee" to invite your first team member.</div>
      ) : (
        <div style={s.table}>
          <div style={s.tableHeader}>
            <span style={{ flex: 2 }}>Name</span>
            <span style={{ flex: 3 }}>Email</span>
            <span style={{ flex: 1.5 }}>Role</span>
            <span style={{ flex: 1.5 }}>Status</span>
            <span style={{ flex: 2 }}>Permissions</span>
            <span style={{ flex: 1.5, textAlign: 'right' }}>Actions</span>
          </div>
          {users.map(u => (
            <div key={u.email} style={s.tableRow}>
              <span style={{ flex: 2, fontWeight: 500 }}>{u.name}</span>
              <span style={{ flex: 3, color: 'rgba(0,0,0,0.5)', fontSize: '0.85rem' }}>{u.email}</span>
              <span style={{ flex: 1.5 }}>
                <span style={u.role === 'customer_admin' ? s.badgeAdmin : s.badgeUser}>
                  {u.role === 'customer_admin' ? 'Admin' : 'User'}
                </span>
              </span>
              <span style={{ flex: 1.5 }}>
                <span style={u.status === 'active' ? s.badgeActive : u.status === 'invited' ? s.badgeInvited : s.badgeDisabled}>
                  {u.status}
                </span>
              </span>
              <span style={{ flex: 2, fontSize: '0.82rem', color: 'rgba(0,0,0,0.5)' }}>
                {u.role === 'customer_admin' ? 'Full access' :
                  u.permissions?.canPurchaseDirectly ? 'Direct purchase' : 'Approval required'}
              </span>
              <span style={{ flex: 2, textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                {u.role !== 'customer_admin' && (
                  <>
                    {u.status === 'invited' && (
                      <button
                        style={{
                          ...s.actionBtnResend,
                          ...(resentEmails[u.email] ? { color: '#16a34a', border: '1px solid #bbf7d0' } : {})
                        }}
                        onClick={() => resendInvite(u)}
                        disabled={resentEmails[u.email]}
                        title="Resend invite email"
                      >
                        {resentEmails[u.email] ? 'Sent ✓' : 'Resend'}
                      </button>
                    )}
                    {u.status === 'active' && (
                      <button
                        style={{
                          ...s.actionBtnResend,
                          ...(resetEmails[u.email] ? { color: '#16a34a', border: '1px solid #bbf7d0' } : {})
                        }}
                        onClick={() => resetPassword(u)}
                        disabled={resetEmails[u.email]}
                        title="Send password reset email"
                      >
                        {resetEmails[u.email] ? 'Sent ✓' : 'Reset Password'}
                      </button>
                    )}
                    <button style={s.actionBtn} onClick={() => togglePermission(u)} title="Toggle purchase permission">
                      {u.permissions?.canPurchaseDirectly ? 'Restrict' : 'Allow'}
                    </button>
                    <button style={s.actionBtnDanger} onClick={() => toggleStatus(u)}>
                      {u.status === 'disabled' ? 'Enable' : 'Disable'}
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.4rem', fontWeight: 700, color: '#333', margin: 0 },
  subtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.45)', margin: '0.25rem 0 0' },
  addBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.2rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  error: { background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 8, padding: '0.75rem 1rem', color: '#c0392b', fontSize: '0.85rem', marginBottom: '1rem' },
  addForm: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  formRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  label: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '0.65rem 0.85rem', fontSize: '0.9rem', outline: 'none' },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: '0.85rem', color: '#444', cursor: 'pointer' },
  submitBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.65rem 1.5rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', alignSelf: 'flex-start' },
  empty: { textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '3rem', fontSize: '0.9rem' },
  table: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '0.75rem 1.25rem', background: '#fafafa', borderBottom: '1px solid #e0e0e0', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  tableRow: { display: 'flex', alignItems: 'center', padding: '0.85rem 1.25rem', borderBottom: '1px solid #f0f0f0', fontSize: '0.88rem' },
  badgeAdmin: { background: '#A50B18', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  badgeUser: { background: '#e8e8e8', color: '#555', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  badgeActive: { background: '#e8f5e9', color: '#2e7d32', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  badgeInvited: { background: '#fff3e0', color: '#e65100', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  badgeDisabled: { background: '#f5f5f5', color: '#999', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  actionBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', color: '#555' },
  actionBtnDanger: { background: 'none', border: '1px solid #fecaca', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', color: '#c0392b' },
  actionBtnResend: { background: 'none', border: '1px solid #bde0fe', borderRadius: 4, padding: '0.25rem 0.6rem', fontSize: '0.75rem', cursor: 'pointer', color: '#2563eb' },
};
